import { createCareer, deleteCareerById, fetchCareerById, updateCareerById } from "@/(admin)/modules/career/services/career"
import getCareers from "@/services/careers"
import { Career, CareerListResponseDataItem, CareerRequest } from "@/types/codegen/data-contracts"
import { useCallback, useState } from "react"

type PaginationType = {
  page?: number
  pageSize?: number
  pageCount?: number
  total?: number
}

export type UseCareersOptions = {
  sort?: string | string[]
}

export const useCareers = (options?: UseCareersOptions) => {
  const [careers, setCareers] = useState<CareerListResponseDataItem[] | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [locale, setLocale] = useState<string>("vi")
  const [careerDetail, setCareerDetail] = useState<Career | null>(null)
  const [pagination, setPagination] = useState<PaginationType | undefined>({
    
    page: 1,
    pageSize: 10,
    total: 0,
    pageCount: 0,
  })

  const fetchCareers = async (
    localeParam?: string,
    filters?: Record<string, any>,
    paginationParams?: PaginationType,
    sortParam?: string | string[]
  ) => {
    setLoading(true)
    try {
      const queryParams: any = {
        locale: localeParam || locale,
        pagination: {
          page: paginationParams?.page || pagination?.page,
          pageSize: paginationParams?.pageSize || pagination?.pageSize,
        },
        filters: filters,
      }

      if (sortParam || options?.sort) {
        queryParams.sort = sortParam || options?.sort
      }

      const response = await getCareers(queryParams)
      setCareers(response.data)
      setPagination(prev => ({
        ...prev,
        ...response.meta?.pagination,
      }))
    } catch (error) {
      setError(error as string)
    } finally {
      setLoading(false)
    }
  }

  const getCareerById = useCallback(async (id: number | string) => {
    setLoading(true);
    try {
      const res = await fetchCareerById(Number(id));
      setCareerDetail(res?.attributes || null);
      return res;
    } finally {
      setLoading(false);
    }
  }, []);

  const createCareerItem = useCallback(async (data: CareerRequest) => {
    setLoading(true);
    try {
      return await createCareer(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCareerItem = useCallback(
    async (id: number, data: CareerRequest) => {
      setLoading(true);
      try {
        return await updateCareerById(id, data);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteCareerItem = useCallback(async (id: number) => {
    setLoading(true);
    try {
      return await deleteCareerById(id);
    } finally {
      setLoading(false);
    }
  }, []);

  return { careers, loading, error, pagination, fetchCareers, setPagination, setLocale, careerDetail, getCareerById, createCareerItem, updateCareerItem, deleteCareerItem }
}
